import repository from '@/repository'
import { ActionTree, Module, MutationTree } from 'vuex'
import { RootState } from '../types'

const getErrorMessage = (err) =>
  err.response?.data?.messageLocalizable || 'requestTimedOut'

const getDefaultState = (): SchedulesState => {
  return {
    schedulesList: [],
    schedulesListIsLoading: false,
    schedulesListErrorMessage: '',
    analyticsSchedulesList: [],
    analyticsSchedulesListIsLoading: false,
    analyticsSchedulesListErrorMessage: '',
    nonStandardDatesList: [],
    nonStandardDatesListIsLoading: false,
    nonStandardDatesListErrorMessage: '',
    saveScheduleIsLoading: false,
    saveScheduleErrorMessage: '',
    saveScheduleValidationError: false,
    saveDateIsLoading: false,
    saveDateErrorMessage: '',
    enableDisableScheduleIsLoading: false,
    enableDisableScheduleError: false,
    enableDisableScheduleErrorMessage: '',
    deleteScheduleIsLoading: false,
    deleteScheduleError: false,
    deleteScheduleErrorMessage: '',
    deleteDateIsLoading: false,
    deleteDateError: false,
    deleteDateErrorMessage: '',
    alertSchedulesList: [],
    alertSechulesListIsLoading: false,
  }
}

const state = getDefaultState()

const mutations: MutationTree<SchedulesState> = {
  reset(state) {
    Object.assign(state, getDefaultState())
  },
  setSchedulesList(state, value) {
    state.schedulesList = value
  },
  setSchedulesListIsLoading(state, value) {
    state.schedulesListIsLoading = value
  },
  setSchedulesListErrorMessage(state, value) {
    state.schedulesListErrorMessage = value
  },
  setAnalyticsSchedulesList(state, value) {
    state.analyticsSchedulesList = value
  },
  setAnalyticsSchedulesListIsLoading(state, value) {
    state.analyticsSchedulesListIsLoading = value
  },
  setAnalyticsSchedulesListErrorMessage(state, value) {
    state.analyticsSchedulesListErrorMessage = value
  },
  setNonStandardDatesList(state, value) {
    state.nonStandardDatesList = value
  },
  setNonStandardDatesListIsLoading(state, value) {
    state.nonStandardDatesListIsLoading = value
  },
  setNonStandardDatesListErrorMessage(state, value) {
    state.nonStandardDatesListErrorMessage = value
  },
  setSaveScheduleIsLoading(state, value) {
    state.saveScheduleIsLoading = value
  },
  setSaveScheduleErrorMessage(state, value) {
    state.saveScheduleErrorMessage = value
  },
  setSaveScheduleValidationError(state, value) {
    state.saveScheduleValidationError = value
  },
  setSaveDateIsLoading(state, value) {
    state.saveDateIsLoading = value
  },
  setSaveDateErrorMessage(state, value) {
    state.saveDateErrorMessage = value
  },
  updateScheduleList(state, value) {
    const currentScheduleIndex = state.schedulesList.findIndex((schedule) => {
      if (schedule.scheduleId == value.scheduleId) {
        return schedule
      }
    })
    if (currentScheduleIndex != -1) {
      state.schedulesList[currentScheduleIndex] = value
    } else {
      state.schedulesList.push(value)
    }
  },
  updateNonStandardDatesList(state, value) {
    const currentDateIndex = state.nonStandardDatesList.findIndex((date) => {
      if (date.definitionId == value.definitionId) {
        return date
      }
    })
    if (currentDateIndex != -1) {
      state.nonStandardDatesList[currentDateIndex] = value
    } else {
      state.nonStandardDatesList.push(value)
    }
  },
  setEnableDisableSchedule(state, value) {
    const currentScheduleList = [
      ...(state.schedulesList || []),
      ...(state.analyticsSchedulesList || []),
    ]
    currentScheduleList.forEach((schedule) => {
      if (schedule.scheduleId == value.scheduleId) {
        schedule.disabled = value.disabled
      }
    })
  },
  setEnableDisableScheduleIsLoading(state, value) {
    state.enableDisableScheduleIsLoading = value
  },
  setEnableDisableScheduleError(state, value) {
    state.enableDisableScheduleError = value
  },
  setEnableDisableScheduleErrorMessage(state, value) {
    state.enableDisableScheduleErrorMessage = value
  },
  setDeleteScheduleIsLoading(state, value) {
    state.deleteScheduleIsLoading = value
  },
  setDeleteScheduleError(state, value) {
    state.deleteScheduleError = value
  },
  setDeleteScheduleErrorMessage(state, value) {
    state.deleteScheduleErrorMessage = value
  },
  setDeleteDateIsLoading(state, value) {
    state.deleteDateIsLoading = value
  },
  setDeleteDateError(state, value) {
    state.deleteDateError = value
  },
  setDeleteDateErrorMessage(state, value) {
    state.deleteDateErrorMessage = value
  },
  setAlertSchedulesList(state, value) {
    state.alertSchedulesList = value
  },
  setAlertSchedulesListIsLoading(state, value) {
    state.alertSechulesListIsLoading = value
  },
}

const actions: ActionTree<SchedulesState, RootState> = {
  async getSchedulesList({ commit }, payload) {
    commit('setSchedulesListIsLoading', true)
    return repository.Schedule.Schedules(payload)
      .then((res) => {
        commit('setSchedulesList', res.data)
      })
      .catch((err) => {
        commit('setSchedulesListErrorMessage', getErrorMessage(err))
      })
      .finally(() => {
        commit('setSchedulesListIsLoading', false)
      })
  },
  async getAnalyticsSchedulesList({ commit }, payload) {
    commit('setAnalyticsSchedulesListIsLoading', true)
    commit('setAnalyticsSchedulesListErrorMessage', '')
    return repository.Schedule.GetAnalyticsSchedules(payload)
      .then((res) => {
        commit('setAnalyticsSchedulesList', res.data)
      })
      .catch((err) => {
        commit('setAnalyticsSchedulesListErrorMessage', getErrorMessage(err))
      })
      .finally(() => {
        commit('setAnalyticsSchedulesListIsLoading', false)
      })
  },
  async saveAnalyticsSchedule({ commit }, payload) {
    const request = payload.scheduleId
      ? repository.Schedule.UpdateAnalyticsSchedule(payload)
      : repository.Schedule.CreateAnalyticsSchedule(payload)
    commit('setSaveScheduleIsLoading', true)
    commit('setSaveScheduleValidationError', false)
    return request
      .then(() => {
        commit('setSaveScheduleErrorMessage', '')
      })
      .catch((err) => {
        commit('setSaveScheduleValidationError', err.response?.status == 400)
        commit('setSaveScheduleErrorMessage', getErrorMessage(err))
      })
      .finally(() => {
        commit('setSaveScheduleIsLoading', false)
      })
  },
  async getNonStandardDatesList({ commit }, payload) {
    commit('setNonStandardDatesListIsLoading', true)
    return repository.Schedule.NonStandardScheduleDefinitions(payload)
      .then((res) => {
        commit('setNonStandardDatesList', res.data)
      })
      .catch((err) => {
        commit('setNonStandardDatesListErrorMessage', getErrorMessage(err))
      })
      .finally(() => {
        commit('setNonStandardDatesListIsLoading', false)
      })
  },
  async saveSchedule({ commit }, payload) {
    commit('setSaveScheduleIsLoading', true)
    commit('setSaveScheduleValidationError', false)
    return repository.Schedule.UpdateSchedule(payload)
      .then(() => {
        commit('updateScheduleList', payload)
        commit('setSaveScheduleErrorMessage', '')
      })
      .catch((err) => {
        if (err.response.status == 400) {
          commit('setSaveScheduleValidationError', true)
          commit('setSaveScheduleErrorMessage', getErrorMessage(err))
        } else {
          commit('setSaveScheduleValidationError', false)
          commit('setSaveScheduleErrorMessage', getErrorMessage(err))
        }
      })
      .finally(() => {
        commit('setSaveScheduleIsLoading', false)
      })
  },
  async saveDate({ commit }, payload) {
    commit('setSaveDateIsLoading', true)
    return repository.Schedule.UpdateNonStandardScheduleDefinition(payload)
      .then((res) => {
        payload.definitionId = res.data
        commit('updateNonStandardDatesList', payload)
        commit('setSaveDateErrorMessage', '')
      })
      .catch((err) => {
        commit('setSaveDateErrorMessage', getErrorMessage(err))
      })
      .finally(() => {
        commit('setSaveDateIsLoading', false)
      })
  },
  async removeDate({ commit }, payload) {
    commit('setSaveDateIsLoading', true)
    return repository.Schedule.UpdateNonStandardScheduleDefinition(payload)
      .then((res) => {
        console.log(res.data)
      })
      .catch((err) => {
        commit('setSaveDateErrorMessage', getErrorMessage(err))
      })
      .finally(() => {
        commit('setSaveDateIsLoading', false)
      })
  },
  async enableDisableSchedule({ commit }, payload) {
    commit('setEnableDisableScheduleIsLoading', true)
    return repository.Schedule.UpdateScheduleStatus(payload)
      .then(() => {
        commit('setEnableDisableSchedule', payload)
      })
      .catch((err) => {
        commit('setEnableDisableScheduleError', true)
        commit('setEnableDisableScheduleErrorMessage', getErrorMessage(err))
      })
      .finally(() => {
        commit('setEnableDisableScheduleIsLoading', false)
      })
  },
  async deleteSchedule({ commit }, payload) {
    commit('setDeleteScheduleIsLoading', true)
    commit('setDeleteScheduleError', false)
    commit('setDeleteScheduleErrorMessage', '')
    return repository.Schedule.DeleteSchedule(payload)
      .then((res) => res.data)
      .catch((err) => {
        commit('setDeleteScheduleError', true)
        commit('setDeleteScheduleErrorMessage', getErrorMessage(err))
        return false
      })
      .finally(() => {
        commit('setDeleteScheduleIsLoading', false)
      })
  },
  clearDeleteScheduleError({ commit }) {
    commit('setDeleteScheduleError', false)
    commit('setDeleteScheduleErrorMessage', '')
  },
  async deleteDate({ commit }, payload) {
    commit('setDeleteDateIsLoading', true)
    commit('setDeleteDateError', false)
    commit('setDeleteDateErrorMessage', '')
    return repository.Schedule.DeleteNonStandardScheduleDefinition(payload)
      .then((res) => res.data)
      .catch((err) => {
        commit('setDeleteDateError', true)
        commit('setDeleteDateErrorMessage', getErrorMessage(err))
        return false
      })
      .finally(() => {
        commit('setDeleteDateIsLoading', false)
      })
  },
  clearDeleteDateError({ commit }) {
    commit('setDeleteDateError', false)
    commit('setDeleteDateErrorMessage', '')
  },
  clearEnableDisableScheduleError({ commit }) {
    commit('setEnableDisableScheduleError', false)
    commit('setEnableDisableScheduleErrorMessage', '')
  },
  getAlertSchedulesList({ commit }) {
    commit('setAlertSchedulesListIsLoading', true)
    const list = [
      { scheduleId: 1, disabled: false, name: 'test alert schedule' },
    ]
    commit('setAlertSchedulesList', list)
    setTimeout(() => {
      commit('setAlertSchedulesListIsLoading', false)
    }, 1000)
  },
}

const Schedules: Module<SchedulesState, RootState> = {
  namespaced: true,
  state,
  mutations,
  actions,
}

export default Schedules

interface SchedulesState {
  schedulesList: Schedule[]
  schedulesListIsLoading: boolean
  schedulesListErrorMessage: string
  analyticsSchedulesList: Schedule[]
  analyticsSchedulesListIsLoading: boolean
  analyticsSchedulesListErrorMessage: string
  nonStandardDatesList: NonStandardDate[]
  nonStandardDatesListIsLoading: boolean
  nonStandardDatesListErrorMessage: string
  saveScheduleIsLoading: boolean
  saveScheduleErrorMessage: string
  saveScheduleValidationError: boolean
  saveDateIsLoading: boolean
  saveDateErrorMessage: string
  enableDisableScheduleIsLoading: boolean
  enableDisableScheduleError: boolean
  enableDisableScheduleErrorMessage: string
  deleteScheduleIsLoading: boolean
  deleteScheduleError: boolean
  deleteScheduleErrorMessage: string
  deleteDateIsLoading: boolean
  deleteDateError: boolean
  deleteDateErrorMessage: string
  alertSchedulesList: Schedule[]
  alertSechulesListIsLoading: boolean
}

interface Schedule {
  scheduleId: number
  disabled: boolean
  armingMode?: number
}

interface NonStandardDate {
  definitionId: number
}
